import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type Reason } from '../api';
import { useCameFrom } from '../hooks';
import { Alert, Avatar as UiAvatar, Badge, Breadcrumb as UiBreadcrumb, Button, EmptyState, SegmentedControl, Skeleton, toast, UiProvider } from '@ledgerloom/ui';
import {
  Activity, ArrowUpDown, Bot, Box, Calendar, ChartColumn, Check, ChevronDown, ChevronLeft, ChevronRight, CircleQuestionMark, Columns3,
  BookOpen, Copy, Download, Ellipsis, FileText, Funnel, GitMerge, House, Inbox, Kanban, List, LogOut, Mail, Megaphone, NotebookPen, Paperclip,
  PanelLeft, Phone, Plus, RotateCcwClock, Rows3, Search, Settings, Shield, Split, SquareCheck, SquareKanban, TriangleAlert, Upload,
  Ticket, UserPlus, Users, UsersRound, X, CircleUser, KeyRound, Plug, type LucideIcon,
} from 'lucide-react';
import { useScheme } from '../theme';

// ---- icons: each name the app uses, drawn by lucide ------------------------
const ICONS = {
  home: House, contacts: Users, inbox: Inbox, activity: Activity, upload: Upload, agent: Bot, settings: Settings,
  plus: Plus, calendar: Calendar, mail: Mail, attach: Paperclip, check: Check, x: X, search: Search, merge: GitMerge,
  split: Split, back: ChevronLeft, download: Download, more: Ellipsis, chevron: ChevronRight, panel: PanelLeft,
  logout: LogOut, copy: Copy, shield: Shield, pipeline: SquareKanban, lead: UserPlus, quote: FileText,
  megaphone: Megaphone, chart: ChartColumn, box: Box, alert: TriangleAlert, records: Rows3, team: UsersRound,
  down: ChevronDown, phone: Phone, note: NotebookPen, help: CircleQuestionMark, history: RotateCcwClock,
  task: SquareCheck, filter: Funnel, sort: ArrowUpDown, columns: Columns3, list: List, board: Kanban,
  user: CircleUser, key: KeyRound, plug: Plug, ticket: Ticket, book: BookOpen,
} satisfies Record<string, LucideIcon>;

// The size is a floor, not the look: styles.css sizes icons per context, and
// without it an icon no rule covers fills its container.
export function Icon({ name }: { name: keyof typeof ICONS | string }) {
  const Drawn = ICONS[name as keyof typeof ICONS];
  return Drawn ? <Drawn size={18} strokeWidth={1.8} aria-hidden="true" /> : null;
}

// ---- proposal states and reasons in plain words ------------------------------
const STATE: Record<string, [string, string]> = {
  written: ['Saved', 'ok'], applied: ['Applied', 'ok'], approved: ['Approved', 'ok'], edited: ['Approved with changes', 'ok'], sent: ['Sent', 'ok'],
  pending_approval: ['Waiting for approval', 'warn'], shadowed: ['Shadow (not saved)', 'info'],
  rejected: ['Rejected', 'danger'], expired: ['Expired', ''], withdrawn: ['Withdrawn', ''], superseded: ['Replaced', ''],
  submitted: ['Submitted', ''], schema_valid: ['Checking', ''], computed: ['Checking', ''], rules_passed: ['Checking', ''],
};

// The app's tone words (ok, warn, danger, info, none) as a Badge tone.
const BADGE_TONE = { ok: 'success', warn: 'warning', danger: 'danger', info: 'info' } as const;
export const badgeTone = (tone: string) => BADGE_TONE[tone as keyof typeof BADGE_TONE] ?? 'neutral';

// An agent's or a person's status: a Badge with the status mark's dot. The tone is the lane
// (attn, stuck, work, trial, idle); the dot's colour means that lane and nothing else.
const LANE: Record<string, { tone: 'warning' | 'danger' | 'success' | 'info' | 'neutral'; dot: string }> = {
  attn: { tone: 'warning', dot: 'bg-[var(--st-attn)]' }, stuck: { tone: 'danger', dot: 'bg-[var(--st-stuck)]' },
  work: { tone: 'success', dot: 'bg-[var(--st-work)]' }, trial: { tone: 'info', dot: 'bg-[var(--st-trial)]' },
  idle: { tone: 'neutral', dot: 'bg-[var(--st-idle)]' },
};
export function ToneBadge({ tone, children }: { tone: string; children: ReactNode }) {
  const lane = LANE[tone] ?? LANE.idle!;
  return <Badge tone={lane.tone} className="gap-1.5"><span className={`size-2 shrink-0 rounded-pill ${lane.dot}`} aria-hidden="true" />{children}</Badge>;
}

export function StateChip({ state }: { state: string }) {
  const [label, tone] = STATE[state] ?? [state, ''];
  return <Badge tone={badgeTone(tone)}>{label}</Badge>;
}

export const SCHEMA_LABEL: Record<string, string> = {
  'ticket.create': 'New ticket', 'ticket.update': 'Change a ticket', 'ticket.assign': 'Assign a ticket', 'ticket.link': 'Link a ticket', 'ticket.time.log': 'Log time on a ticket',
  'ticket.message.internal': 'Note on a ticket', 'ticket.message.reply': 'Reply on a ticket', 'ticket.merge': 'Merge tickets', 'ticket.mail.received': 'Mail on a ticket', 'ticket.message.sent': 'Reply on a ticket', 'ticket.sla.breach': 'Missed service level',
  'config.ticket_type': 'Ticket type', 'config.ticket_queue': 'Ticket queue', 'config.sla_policy': 'Service level', 'config.ticket_view': 'Shared ticket view',
  'party.create': 'New contact', 'party.update': 'Edit contact', 'party.merge': 'Merge contacts',
  'party.mark_distinct': 'Not duplicates', 'party.set_stage': 'Lifecycle stage', 'party.bulk_edit': 'Bulk edit', 'relationship.create': 'Link contacts',
  'note.create': 'Add note', 'import.batch': 'Import', 'config.feed': 'Feed', 'config.webhook_events': 'Events a webhook gets', 'connection.probe': 'Look at a connection', 'config.custom_field': 'Custom fields', 'config.role': 'Roles and permissions', 'config.role_grant': 'Give or take back a role', 'config.scope_predicate': 'Access scope', 'config.field_policy': 'Field visibility', 'config.sod_rule': 'Separation of duties', 'config.approval_matrix': 'Who approves',
  'config.agent_status': 'Agent status', 'gl.journal_entry_proposal': 'Journal entry', 'books.trial_balance.import': 'Imported trial balance', 'config.books_cutover': 'Cut over to these books', 'gl.journal_entry.post': 'Journal entry', 'gl.journal_entry.reverse': 'Reverse a journal entry', 'gl.period.close': 'Close a fiscal period', 'gl.period.reopen': 'Reopen a fiscal period', 'gl.year_end.close': 'Close a fiscal year', 'config.chart_of_accounts': 'Chart of accounts', 'config.fiscal_calendar': 'Fiscal calendar',
  'config.agent_hire': 'Hire an agent', 'config.agent_onboard': 'Tune an agent', 'config.agent_envelope': 'Autonomy change', 'config.agent_group': 'Agent group',
  'config.playbook_sign': 'Sign a playbook', 'config.playbook_version': 'New playbook version', 'config.playbook_author': 'New agent playbook', 'person.ask': 'Question for a person', 'notice.post': 'Notice', 'notice.close': 'Close a notice', 'config.tenant': 'Workspace settings',
  'browser.session': 'Browser session', 'site.change': 'Website change', 'config.playbook_pin': 'Pin a standard playbook version', 'config.playbook_upgrade': 'Upgrade an agent to a new version', 'config.playbook_override': 'Override a standard playbook',
  'config.dashboard': 'Dashboard', 'config.form': 'Form', 'config.pipeline': 'Pipeline', 'config.lead_routing': 'Lead routing',
  'config.score_model': 'Lead score model', 'config.subscription_types': 'Subscription types', 'config.brand_profile': 'Brand profile',
  'config.contact_lifecycle': 'Contact lifecycle', 'config.contact_classes': 'Contact classes', 'config.contact_grades': 'Contact grades',
  'lead.create': 'New lead', 'lead.update': 'Edit lead', 'lead.convert': 'Convert lead', 'deal.create': 'New deal', 'deal.update': 'Edit deal',
  'deal.move_stage': 'Move deal', 'deal.close': 'Close deal', 'activity.create': 'New activity', 'activity.update': 'Update activity',
  'product.create': 'New product', 'product.update': 'Edit product', 'quote.create': 'New quote', 'quote.submit': 'Submit quote',
  'quote.accept': 'Accept quote', 'quote.decline': 'Decline quote', 'quote.mark_sent': 'Send quote',
  'comms.email_send': 'Send email', 'comms.email_broadcast': 'Email broadcast', 'calendar.event_write': 'Calendar event', 'webhook.call': 'Webhook call',
  'consent.record': 'Consent', 'consent.import': 'Import consent', 'subscription.change': 'Subscription',
  'campaign_program.create': 'New campaign', 'campaign_program.update': 'Edit campaign', 'segment.create': 'New segment', 'segment.update': 'Edit segment',
  'email.create': 'New email', 'email.update': 'Edit email', 'landing_page.create': 'New landing page', 'landing_page.update': 'Edit landing page',
  'sequence.create': 'New sequence', 'sequence.enroll': 'Enroll in sequence', 'sequence.stop': 'Stop sequence', 'experiment.decide': 'Experiment result',
  'asset.create': 'New template', 'asset.version': 'New template version', 'asset.status': 'Template status',
  'memory.capture': 'Capture judgment', 'bench.advisory': 'Benchmark advisory', 'memory.promote': 'Promote judgment', 'file.attach': 'Attach file',
};
export const schemaLabel = (s: string) => SCHEMA_LABEL[s] ?? s;

// Rule reasons are written for agents. People get plain wording; the rule id stays visible.
const FOR_PEOPLE: Record<string, string> = {
  'dedup.duplicate_blocks_create': 'This contact is already in Mazu. Open the existing record and add to it instead.',
  'dedup.possible_needs_review': 'This looks like someone already in Mazu, so a person should confirm it is new.',
  'merge.stale': 'One of these records changed since the merge was proposed. Try again.',
  'merge.weak_match': 'The dedup engine does not rate these records as duplicates.',
  'refs.missing': 'A linked contact or person no longer exists.',
  'refs.inactive': 'A linked contact has been archived or merged.',
  'import.unknown_owners': 'Some rows name an owner who is not on your team yet. Add them first.',
};

// A rule's text points agents at the facts behind it ("(see computed.x.error)"); people need only the sentence.
export const forPeople = (text: string) => text.replace(/\s*\(see computed\.[^)]*\)/g, '');
export const reasonText = (r: Reason) => (r.rule_id && FOR_PEOPLE[r.rule_id]) ?? forPeople(r.message);

export function Reasons({ reasons }: { reasons: Reason[] }) {
  if (!reasons?.length) return null;
  return (
    <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
      {reasons.map((r, i) => <li key={i}>{reasonText(r)}{r.rule_id ? <span className="faint"> · {r.rule_id}</span> : null}</li>)}
    </ul>
  );
}

// role="alert" so a failure is announced; focusOnShow moves focus to it after a submit.
export function ErrorBanner({ error, focusOnShow = false }: { error: unknown; focusOnShow?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error && focusOnShow) ref.current?.focus(); }, [error, focusOnShow]);
  if (!error) return null;
  const e = error as ApiError;
  const details = Array.isArray(e.details) ? (e.details as Array<{ path: string; message: string }>) : null;
  return (
    <Alert tone="danger" ref={ref} tabIndex={-1}>
      {e.message || 'Something went wrong.'}
      {details && <ul className="mt-1.5 list-disc pl-[18px]">{details.slice(0, 5).map((d, i) => <li key={i}>{fieldName(d.path)} {d.message}</li>)}</ul>}
    </Alert>
  );
}

// "/emails_add/0/address" -> "email address"
const fieldName = (path: string) => path.split('/').filter((x) => x && !/^\d+$/.test(x)).pop()?.replace(/_add$|_remove$/, '').replace(/_/g, ' ') ?? '';

// A block of placeholder lines where a record or panel will be. The .skel class
// marks a placeholder for the browser tests, which wait for none to be left.
export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid gap-3 p-6" role="status">
      <span className="sr-only">{label}…</span>
      <Skeleton className="skel h-5 w-1/3" /><Skeleton className="skel h-3 w-2/3" /><Skeleton className="skel h-3 w-1/2" />
    </div>
  );
}

// Placeholder rows shaped like a list, so the page does not jump when data arrives.
export function ListSkeleton({ rows = 6, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div role="status">
      <span className="sr-only">{label}…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3.5 border-t border-line px-5 py-3 first:border-t-0" aria-hidden="true">
          <Skeleton className="skel size-[34px] shrink-0 rounded-pill" />
          <div className="grid flex-1 gap-1.5"><Skeleton className="skel h-3 w-2/5" /><Skeleton className="skel h-2.5 w-[65%]" /></div>
        </div>
      ))}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <EmptyState title={title} description={children} action={action} />;
}

// A 0 to 1 match score as a short bar with its value; a meter, not progress toward a goal.
export function Score({ value, label = 'Match score' }: { value: number; label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-[13px] tabular-nums" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={1} aria-valuenow={value} aria-valuetext={value.toFixed(2)}>
      <span className="h-1.5 w-16 overflow-hidden rounded-pill bg-surface-muted" aria-hidden="true"><span className="block h-full rounded-pill bg-[var(--chart)]" style={{ width: `${Math.round(value * 100)}%` }} /></span>
      {value.toFixed(2)}
    </span>
  );
}

export function initials(name: string) {
  return name.split(/\s+/).filter((w) => /[A-Za-z]/.test(w[0] ?? '')).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';
}

// Beside the contact's name, so decorative. An organization is square, a household its own tint.
const AVATAR_KIND: Record<string, string> = {
  organization: 'rounded-control border-transparent bg-[var(--info-subtle)] text-info',
  household: 'border-transparent bg-[var(--warning-subtle)] text-warning',
};
export function Avatar({ name, kind }: { name: string; kind: string }) {
  return <UiAvatar decorative name={name} className={`size-[34px] shrink-0 border-transparent bg-accent-subtle font-bold text-accent-strong ${AVATAR_KIND[kind] ?? ''}`} />;
}

export const when = (iso: string) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
};

// A relative time that keeps the exact moment for assistive tech and on hover.
export function Time({ iso, full = false }: { iso: string; full?: boolean }) {
  const d = new Date(iso);
  return <time dateTime={iso} title={d.toLocaleString()}>{full ? d.toLocaleString() : when(iso)}</time>;
}

// ---- disclosure, toggle group, breadcrumb --------------------------------------
export function Expandable({ label, children, defaultOpen = false }: { label: [string, string]; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <>
      <Button variant="ghost" size="sm" press="tint" className="self-start px-0" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        <ChevronRight aria-hidden="true" size={16} className={`transition-transform motion-reduce:transition-none ${open ? 'rotate-90' : ''}`} />
        {open ? label[1] : label[0]}
      </Button>
      <div id={id} hidden={!open}>{open && children}</div>
    </>
  );
}

// One of a few views or filters, all visible (the library's segmented control: a radio group).
export function ToggleGroup<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return <SegmentedControl label={label} value={value} onValueChange={(v) => onChange(v as T)} segments={options.map(([v, text]) => ({ value: v, label: text }))} />;
}

// The trail starts at the page the person came from: a step of the trail keeps
// what they had there (its filters), and a page off the trail replaces it.
export function Breadcrumb({ items }: { items: Array<{ to?: string; label: ReactNode }> }) {
  const from = useCameFrom();
  const onTrail = (it: { to?: string }) => !!from && it.to?.split('?')[0] === from.pathname;
  const trail = !from ? items : items.some(onTrail) ? items.map((it) => (onTrail(it) ? { ...it, to: from.to } : it)) : [{ to: from.to, label: from.label }, items[items.length - 1]!];
  const crumbs = trail.map((it, i) => ({ href: it.to, label: i === 0 && it.to ? <><Icon name="back" />{it.label}</> : it.label }));
  return <UiBreadcrumb className="mb-3" items={crumbs} link={RouterLink} />;
}
const RouterLink = ({ href, ...props }: { href: string; className: string; children: ReactNode }) => <Link to={href} {...props} />;

// ---- toast -------------------------------------------------------------------
// Both live regions stay mounted so screen readers announce every change, and a
// failure goes to the assertive one. The card on screen is the library's toaster,
// hidden from assistive tech so nothing is read twice; a failure waits to be
// dismissed (its button, or Escape). Pattern: user-feedback/toast.
type Tone = 'ok' | 'error';
const ToastCtx = createContext<(msg: string, tone?: Tone) => void>(() => undefined);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [said, setSaid] = useState<{ msg: string; tone: Tone } | null>(null);
  const failure = useRef<string | number | null>(null);
  const show = useCallback((msg: string, tone: Tone = 'ok') => {
    setSaid({ msg, tone });
    const forget = () => setSaid((cur) => (cur?.msg === msg ? null : cur));
    const id = toast.custom((t) => (
      <div aria-hidden="true" className={`flex max-w-[calc(100vw-32px)] items-center gap-3 rounded-control px-4 py-2.5 text-sm shadow-floating ${tone === 'error' ? 'bg-danger text-white' : 'bg-foreground text-background'}`}>
        <span>{msg}</span>
        {tone === 'error' && <button type="button" tabIndex={-1} className="rounded-control px-2 py-1 font-medium can-hover:hover:bg-white/15" onClick={() => { toast.dismiss(t); forget(); }}>Dismiss</button>}
      </div>
    ), { duration: tone === 'error' ? Infinity : 5000, onAutoClose: forget });
    if (tone === 'error') failure.current = id;
  }, []);
  useEffect(() => {
    if (said?.tone !== 'error') return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && failure.current !== null) { toast.dismiss(failure.current); failure.current = null; setSaid(null); } };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [said]);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="sr-only" role="status" aria-live="polite">{said?.tone === 'ok' ? said.msg : ''}</div>
      <div className="sr-only" role="alert" aria-live="assertive">{said?.tone === 'error' ? said.msg : ''}</div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// The UI library around an app: its motion settings and its toaster in the system scheme.
// Toasts sit bottom centre, above the phone's tab bar and clear of the help button.
export const UiRoot = ({ children }: { children: ReactNode }) => (
  <UiProvider theme={useScheme()} toaster={{ position: 'bottom-center', offset: 24, mobileOffset: { bottom: 88 } }}>{children}</UiProvider>
);

// What happened to a proposal, in one sentence for a toast.
export function outcomeMessage(o: { state: string; reasons?: Reason[] }, saved = 'Saved') {
  if (o.state === 'written' || o.state === 'applied' || o.state === 'approved') return saved;
  if (o.state === 'pending_approval') return 'Sent for approval';
  if (o.state === 'rejected') {
    const r = o.reasons?.[0];
    return `Not saved: ${r ? reasonText(r) : 'a rule refused it'}`;
  }
  return STATE[o.state]?.[0] ?? 'Done';
}
export const outcomeTone = (o: { state: string }): Tone => (o.state === 'rejected' ? 'error' : 'ok');
